import dayjs, { ConfigType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';

dayjs.extend(localeData);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

/**
 * Formats a member's first payment date for display as MM/DD/YYYY.
 *
 * Parses in UTC so a UTC-midnight or date-only backend value (e.g. `members_member.firstpayment`)
 * isn't shifted to the previous calendar day for viewers west of UTC. Returns '' for
 * empty/unset or unparseable values.
 */
export const formatFirstPaymentDate = (firstpayment?: string | null): string => {
    if (!firstpayment) {
        return '';
    }

    const parsed = dayjs.utc(firstpayment);

    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
};

export const numberWithCommas = (number: number | string) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const moneyWithCommas = (number?: number | string) => {
    if (number === undefined || number === null) {
        return;
    }

    return `$${numberWithCommas(number)}`;
};

export const moneyFormat = (value: number, { precision = 2, minPrecision = 0 } = {}) => {
    return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: precision,
        minimumFractionDigits: minPrecision,
        style: 'currency',
        currency: 'USD',
    }).format(value);
};

export const twoDigitsMoneyFormat = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumSignificantDigits: 2,
        style: 'currency',
        currency: 'USD',
    }).format(value);
};

export const dateFormat = (dateString: ConfigType) => dayjs(dateString).format('ddd MMM Do');

export const timeFormat = (utc_deadline: string | number | Date) => {
    const dateVar = new Date(utc_deadline);

    return dateVar.toLocaleTimeString('en-us', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    });
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const dateFormatFromString = (
    utc_deadline: string | number | Date,
    options: Partial<Intl.DateTimeFormatOptions> = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }
) => {
    // A date-only string parses as UTC midnight, which formats as the previous day west of UTC.
    // It names a calendar day rather than an instant, so anchor it to local midnight instead.
    const date = DATE_ONLY_PATTERN.test(String(utc_deadline))
        ? new Date(`${utc_deadline}T00:00:00`)
        : new Date(utc_deadline);

    return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const nthDay = (n?: number) => {
    if (n === undefined || n === null) {
        return;
    }

    return dayjs.localeData().ordinal(n);
};

export const capitalizeFirstLetter = (s?: string) => {
    if (!s) {
        return;
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
};

// Convert 2023-03-14T09:29:29.053132-07:00 to 1/12/24 at 11:11 pm PST
export const convertDate = (date: string | number | Date) => {
    return dayjs(date).format('M/D/YY [at] h:mm a [PST]');
};

export const truncateString = (str: string, num: number) => {
    if (!str) {
        return '';
    }
    // If the length of str is less than or equal to num
    // just return str--don't truncate it.
    if (str.length <= num) {
        return str;
    }
    // Return str truncated with '...' concatenated to the end of str.
    return `${str.slice(0, num)}...`;
};
